import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { useIdleTimer } from "react-idle-timer";
import { faro, TransportItem } from "@grafana/faro-web-sdk";
import { AuthProvider, useAuth, AuthProviderProps, hasAuthParams } from 'react-oidc-context';
import "bootstrap-sass/assets/javascripts/bootstrap.js";
import configureStore from './store';
import { initialState } from './reducers';
import CoreApp from './CoreApp';
import { Provider } from 'react-redux';
import DocumentTitle from '../misc/components/DocumentTitle';
import { InitFontLibrary } from './FontAwesomeLibrary';
import { CookiesProvider } from 'react-cookie';
import { BrowserRouter, useLocation, useNavigate, createRoutesFromChildren, matchRoutes, Routes, useNavigationType } from 'react-router-dom';
import LoadingWithMessage from '../misc/components/LoadingWithMessage';
import { SessionExpiringModal } from '../Account/SessionExpiringModal';
import { User, WebStorageStateStore } from 'oidc-client-ts';
import { Unauthorized } from '../errors/unauthorized/Unauthorized';
import { IdleTimerContextProvider } from './IdleTimerContext';
import { AboutContextProvider, AboutModel } from './AboutContextProvider';
import { WebPortalLegacyClientInternal } from '../misc/api/WebPortalTokenClient';
import { SignInError } from '../errors/SignInError';
import {
    FaroErrorBoundary,
    getWebInstrumentations,
    initializeFaro,
    ReactIntegration,
    ErrorsInstrumentation
} from '@grafana/faro-react';
import Alert, { AlertCategoryEnum } from '../misc/components/Alert';

const store = configureStore(initialState);

InitFontLibrary();

declare const rootUrl: string;


const ApplicationRoot = () => {
    return (
        <DocumentTitle title="Vitalograph Web Portal">
            <BrowserRouter>
                <Root baseRoot={rootUrl} />
            </BrowserRouter>
        </DocumentTitle>
    )
}

export const SaveCurrentPath = (location) => {
    
    const currentPath = location.pathname;
    if (currentPath.includes("/account/study") || currentPath.includes("/unauthorized"))
        return;

    window.sessionStorage.setItem("WebPortal.PreviousPath", currentPath);
}

interface AuthorizedProps {
    baseRoot: string;
    logoutRedirectUri: string;
    children: any;
}

const Authorized = ({ baseRoot, logoutRedirectUri, children }: AuthorizedProps) => {
    const { user, isLoading, activeNavigator, error, events, isAuthenticated, signinRedirect, signoutRedirect } = useAuth();

    const location = useLocation();

    const [sessionExpiryVisible, setSessionExpiryVisible] = React.useState(false);

    const onIdle = () => {
        setSessionExpiryVisible(true);
    }

    const timer = useIdleTimer({
        name: "WebPortalIdleTimer",
        onIdle,
        timeout: 1000 * 60 * 15,
    });

    React.useEffect(() => {
        events.addAccessTokenExpired(() => {
            faro?.api?.resetUser();
            signoutRedirect({ post_logout_redirect_uri: `${baseRoot}${logoutRedirectUri}` });
        });
    }, [events, signoutRedirect]);

    React.useEffect(() => {
        SaveCurrentPath(location);
        if (!hasAuthParams() && !isAuthenticated && !activeNavigator && !isLoading) {
            signinRedirect({ redirectMethod: "replace" });
        }
        
    }, [location, isAuthenticated, activeNavigator, isLoading, signinRedirect]);

    switch (activeNavigator) {
        case "signinRedirect":
            return <LoadingWithMessage message="Redirecting to sign in..." />;
        case "signoutRedirect":
            return <LoadingWithMessage message="Signing you out..." />;
    }

    if (error) {
        return <SignInError />
    }


    if (!user && isLoading)
        return <LoadingWithMessage message="Loading..." />;

    if (!isAuthenticated)
        return <Unauthorized />;

    return (<>
        {sessionExpiryVisible ? <SessionExpiringModal logoutRedirectUri={logoutRedirectUri} onClose={() => setSessionExpiryVisible(false)} /> : null}
        <IdleTimerContextProvider timer={timer}>
            {children}
        </IdleTimerContextProvider>
    </>);
}

interface RootProps {
    baseRoot: string;
}

const FaroWrap = ({ condition, wrap, children }) => (
    condition ? wrap(children) : children
);

const Root = ({ baseRoot }: RootProps) => {
    const navigate = useNavigate();
    const [about, setAbout] = React.useState<AboutModel>();


    React.useEffect(() => {
        const init = async () => {
            const result: AboutModel = await fetch(rootUrl + "about")
                .then(response => response.json());

            setAbout(result);

            if (result.Faro.Enabled) {
                let bots =
                    '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)';
                let botsRegex = new RegExp(bots, 'i');

                initializeFaro({
                    url: result.Faro.CollectorUrl,

                    app: {
                        name: 'Vitalograph.WebPortal.Frontend',
                        version: result.Version
                    },

                    sessionTracking: {
                        enabled: true,
                        persistent: true,
                        samplingRate: botsRegex.test(navigator.userAgent) ? 0 : 1
                    },

                    instrumentations: [
                        ...getWebInstrumentations({
                            captureConsole: true,
                        }),

                        new ReactIntegration(),
                    ],

                    beforeSend(item: TransportItem) {
                        delete item.meta.sdk;
                        return item;
                    },
                });
            }

            
        }

        init();
    }, []);


    const handleSignIn = async (user: User) => {
        await handlePostLogin(user);

        if (window.location.pathname === "/underapproval" || window.location.pathname === "/unauthorized")
            return;

        const prevPath = window.sessionStorage.getItem("WebPortal.PreviousPath");
        if (prevPath === "/account/signedout") {
            navigate('/account/study', { replace: true });
            return;
        }

        window.location.replace(prevPath ?? '/account/study');
    }


    const handlePostLogin = async (user: User) => {
        if (!user) return;

        const legacyClient = new WebPortalLegacyClientInternal(rootUrl, {
            fetch: (url, init) => {
                const authInit = user?.access_token
                    ? {
                        ...init, credentials: 'same-origin',
                        headers: { ...init?.headers, 'Authorization': `Bearer ${user?.access_token}` }
                    } as RequestInit
                    : init;

                return fetch(url, authInit);
            }
        });

        await legacyClient.postLogin().catch(error => {
            console.log(error)
            if (error === "User requires approval")
                navigate("/underapproval");
            else
                navigate("/unauthorized");
            return;
        });
        await legacyClient.LogSignInAuditTrail(false);

        if (about.Faro.Enabled) {
            faro.api.setUser({
                id: user.profile.sub,
                email: user.profile.email,
            });
        }
    }

    if (!about)
        return <LoadingWithMessage message="Loading..." />;

    const oidcConfiguration: AuthProviderProps = {
        authority: about.Authority,
        client_id: about.ClientId,
        scope: about.Scopes,
        redirect_uri: `${baseRoot}${about.SignInRedirect}`,
        automaticSilentRenew: true,
        loadUserInfo: true,
        revokeTokensOnSignout: true,
        accessTokenExpiringNotificationTimeInSeconds: 60,
        onSigninCallback: handleSignIn,
        userStore: new WebStorageStateStore({ store: window.sessionStorage, prefix: "oidc.vitalographwebportal" }),
        stateStore: new WebStorageStateStore({ store: window.sessionStorage, prefix: "oidc.vitalographwebportal" }),
    };

    (window as any).accountServerUrl = about.Authority;
    (window as any).coughMarshalRootUrl = about.CoughMarshalUri;

    return (
        <FaroWrap condition={about.Faro.Enabled} wrap={(children) => <FaroErrorBoundary fallback={<Alert show={true} category={AlertCategoryEnum.Danger}>
            Unfortunately an error has occurred.  The error details have been recorded. We apologize for any inconvenience caused. Please let us know by contacting Vitalograph Support Services
        </Alert>}>{children}</FaroErrorBoundary>}>
            <AuthProvider {...oidcConfiguration}>
                <Authorized baseRoot={baseRoot} logoutRedirectUri={about.SignOutRedirect}>
                    <AboutContextProvider aboutProp={about}>
                        <CoreApp version={about.Version} logoutRedirectUri={about.SignOutRedirect} />
                    </AboutContextProvider>
                </Authorized>
            </AuthProvider>
        </FaroWrap>
    );
}

const container = document.getElementById('coreRoot');
const root = createRoot(container!);

root.render(
    <Provider store={store as any}>
        <CookiesProvider>
            <ApplicationRoot />
        </CookiesProvider>
    </Provider>
);
